import { SelectionState } from "draft-js";
import { updateEntity, insertHtml, concurrentTasks } from "lib/helpers";
import { insertImportAtomic } from "./insertImportAtomic";
import { cleanHtml } from "./cleanHtml";
import { createResultBuilder } from "./resultBuilder";

/**
 * @typedef {Object} TaskContext
 * @property {DraftEditorStore} store
 * @property {File} docFile
 * @property {Draft.SelectionState} selection
 * @property {number} maxFileSize
 * @property {function} createImportTask
 */

/**
 * @param {TaskContext} context
 */
export async function runImportTask(context) {
	const {
		store,
		file,
		fileType,
		selection,
		maxFileSize,
		createImportTask,
	} = context;

	// 插入导入进度块到文档流中
	const { entityKey, block } = insertImportAtomic(store, file.name, fileType, selection);


	const { getEditorState, setEditorState } = store;

	// 执行任务前，要延时一下，等待导入块已经进入到文档流
	await new Promise(resolve => setTimeout(resolve, 1));

	// 用于更新已经插入的进度快
	const update = patch => {
		// 找不到导入块，表示用户已取消
		const content = store.getEditorState().getCurrentContent();
		const atomicBlock = content.getBlockForKey(block.getKey());
		if (!atomicBlock || !atomicBlock.getEntityAt(0)) {
			throw { canceled: true };
		}
		updateEntity({
			entityKey,
			getEditorState,
			setEditorState,
			update: patch,
		});
	};

	// 超过尺寸的任务不执行
	if (file.size > maxFileSize) {
		update({
			error: {
				type: 'SizeLimitExcceed',
				size: file.size,
				maxSize: maxFileSize,
			}
		});
		return;
	}

	update({ started: true });

	try {
		// 提交转换任务
		const { paged, query } = await createImportTask(file, fileType);

		// 用于构建结果
		const resultBuilder = createResultBuilder(file.name, fileType);

		// 分页查询逻辑
		if (paged) {
			// 查询每一页的返回结果，并更新
			let page = 1, pageCount = 1;
			update({ page });

			// 获取第一页时获取页码
			const preview = await query({ page });
			page = 2;
			pageCount = preview.pageCount;
			update({ pageCount });
			resultBuilder.next(preview);

			// 并发获取后续页面
			const tasks = Array.from(Array(pageCount - 1)).map(
				(value, index) => () => query({ page: index + 2 }),
			);
			const concurrent = concurrentTasks(tasks, 5);
			while (page <= pageCount) {
				update({ page });
				const preview = await concurrent.next();
				resultBuilder.next(preview);
				page++;
			}
		}
		// 非分页查询逻辑
		else {
			let progress = 0;
			while (progress < 1) {
				const current = await query();
				progress = current.progress;
				update({ progress });
				resultBuilder.next(current);
			}
		}

		resultBuilder.execute(store, block);
		update({ done: true });
	}
	catch (err) {
		if (err.canceled) {
			return;
		}
		update({
			converting: false,
			error: {
				type: 'ConvertError',
				detail: err.msg || err.message
			}
		});
		console.error(err);
	}
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/fileImportPlugin/modifier/runImportTask.js